import { createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance, { isAxiosError } from "@/utils/axiosInstance";
import { Params } from "@/types";
import { handleThunkError } from "@/utils/thunkErrorHandler";
import { useCache } from "@/utils/cache";

const endpoint = {
  packages: "/package",
  payments: "/payments/packages",
};

export const fetchPackages = createAsyncThunk(
  "packages/fetchPackages",
  async (params: Params, thunkAPI) => {
    try {
      const response = await axiosInstance.get(endpoint.packages, {
        params,
      });
      return response.data;
    } catch (error) {
      return handleThunkError(
        error,
        thunkAPI,
        "Não foi possível carregar os pacotes",
      );
    }
  },
);

export const createPackage = createAsyncThunk(
  "packages/createPackage",
  async (data: any, thunkAPI) => {
    try {
      const isFormData =
        typeof FormData !== "undefined" && data instanceof FormData;
      const response = await axiosInstance.post(endpoint.packages, data, {
        headers: isFormData
          ? { "Content-Type": "multipart/form-data" }
          : undefined,
      });
      return response.data;
    } catch (error) {
      console.log(error);

      return handleThunkError(
        error,
        thunkAPI,
        "Não foi possível cadastrar o serviço",
      );
    }
  },
);

export const updatePackage = createAsyncThunk(
  "packages/updatePackage",
  async (data: any, thunkAPI) => {
    try {
      const { id, ...rest } = data;
      const payload = (rest as any).data ? (rest as any).data : rest;
      const isFormData =
        typeof FormData !== "undefined" && payload instanceof FormData;
      const url = isFormData
        ? `${endpoint.packages}/${id}?_method=PUT`
        : `${endpoint.packages}/${id}`;
      const method = isFormData ? "post" : "put";
      // @ts-ignore
      const response = await axiosInstance[method](url, payload, {
        headers: isFormData
          ? { "Content-Type": "multipart/form-data" }
          : undefined,
      });
      return response.data;
    } catch (error) {
      return handleThunkError(
        error,
        thunkAPI,
        "Não foi possível atualizar o pacote",
      );
    }
  },
);

export const deletePackage = createAsyncThunk(
  "packages/deletePackage",
  async (slug: string, thunkAPI) => {
    try {
      const response = await axiosInstance.delete(
        `${endpoint.packages}/${slug}`,
      );
      return response.data;
    } catch (error) {
      return handleThunkError(
        error,
        thunkAPI,
        "Não foi possível cadastrar o serviço",
      );
    }
  },
);

// Activate a package
export const activatePackage = createAsyncThunk(
  "packages/activatePackage",
  async (id: string | number, thunkAPI) => {
    try {
      const { getEstablishmentSlug } = useCache();
      const establishmentSlug = getEstablishmentSlug() || "";
      const url = establishmentSlug
        ? `/${establishmentSlug}${endpoint.packages}/${id}/activate`
        : `${endpoint.packages}/${id}/activate`;

      const response = await axiosInstance.post(url);
      return response.data;
    } catch (error) {
      return handleThunkError(
        error,
        thunkAPI,
        "Não foi possível ativar o pacote",
      );
    }
  },
);

// Deactivate a package
export const deactivatePackage = createAsyncThunk(
  "packages/deactivatePackage",
  async (id: string | number, thunkAPI) => {
    try {
      const { getEstablishmentSlug } = useCache();
      const establishmentSlug = getEstablishmentSlug() || "";
      const url = establishmentSlug
        ? `/${establishmentSlug}${endpoint.packages}/${id}/deactivate`
        : `${endpoint.packages}/${id}/deactivate`;

      const response = await axiosInstance.post(url);
      return response.data;
    } catch (error) {
      return handleThunkError(
        error,
        thunkAPI,
        "Não foi possível desativar o pacote",
      );
    }
  },
);

export const fetchPaymentPackage = createAsyncThunk(
  "packages/fetchPaymentPackage",
  async (params: Params, thunkAPI) => {
    try {
      const response = await axiosInstance.get(endpoint.payments, {
        params,
      });
      return response.data;
    } catch (error) {
      return handleThunkError(
        error,
        thunkAPI,
        "Não foi possível cadastrar o serviço",
      );
    }
  },
);

export const createPaymentPackage = createAsyncThunk(
  "packages/createPaymentPackage",
  async (data: any, thunkAPI) => {
    try {
      const payload = {
        ...(data || {}),
        validity_months: (data && data.validity_months) ?? 3,
      };
      const response = await axiosInstance.post(endpoint.payments, payload);
      return response.data;
    } catch (error) {
      return handleThunkError(
        error,
        thunkAPI,
        "Não foi possível cadastrar o serviço",
      );
    }
  },
);

export const updatePaymentPackage = createAsyncThunk(
  "packages/updatePaymentPackage",
  async (data: any, thunkAPI) => {
    try {
      const response = await axiosInstance.put(
        `${endpoint.payments}/${data.id}`,
        data,
      );
      return response.data;
    } catch (error) {
      return handleThunkError(
        error,
        thunkAPI,
        "Não foi possível cadastrar o serviço",
      );
    }
  },
);

export const deletePaymentPackage = createAsyncThunk(
  "packages/deletePaymentPackage",
  async (id: string, thunkAPI) => {
    try {
      const response = await axiosInstance.delete(`${endpoint.payments}/${id}`);
      return response.data;
    } catch (error) {
      return handleThunkError(
        error,
        thunkAPI,
        "Não foi possível cadastrar o serviço",
      );
    }
  },
);
